import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useList } from '../hooks/useList';
import { PageHeader, Badge, Button, Modal, Field, Input, Select, Textarea, Money } from '../components/ui';
import { DataTable, type Column } from '../components/ui/DataTable';
import { ListFilter } from '../components/ui/FilterMenu';
import { FormError, Confirm } from '../components/forms/common';
import { Can } from '../components/Can';
import { useCan } from '../hooks/usePermission';
import type { Item, TaxRate, CreateItemBody, UpdateItemBody } from '../types/api';

/** UK only on screen for now; the AE items stay in the table and keep working for AE companies. */
const REGION = 'UK' as const;
const TYPES: Array<{ value: Item['invoiceType']; label: string }> = [
  { value: 'RENT', label: 'Rent' }, { value: 'SERVICE_CHARGE', label: 'Service charge' }, { value: 'RECHARGE', label: 'Recharge' },
  { value: 'DEPOSIT', label: 'Deposit' }, { value: 'SALE', label: 'Sale' },
];
const typeLabel = (t: string) => TYPES.find((x) => x.value === t)?.label ?? t;
const pct = (n: number | null) => (n === null ? '—' : `${n.toFixed(2).replace(/\.00$/, '')}%`);

type Entity = { id: string; name: string; region: string };
type Account = { id: string; entityId: string; code: string; name: string };

/**
 * The things a company invoices for. Each item carries a default price, VAT code and income account; an invoice line
 * that names an item takes whatever it leaves out. Shared items are offered to every company; a company item to one.
 */
export default function ItemsPage() {
  const qc = useQueryClient();
  const list = useList<Item>('/items', { region: REGION, includeInactive: true });
  const [editing, setEditing] = useState<Item | 'new' | null>(null);
  const rates = useQuery({ queryKey: ['/tax-rates', REGION, false], queryFn: async () => (await api.get<TaxRate[]>('/tax-rates', { region: REGION })).data });
  const codes = useMemo(() => [...new Set((rates.data ?? []).filter((r) => r.inForce).map((r) => r.code))].sort(), [rates.data]);
  const refresh = () => qc.invalidateQueries({ queryKey: ['/items'] });

  const columns: Column<Item>[] = [
    { key: 'code', label: 'Code', sortKey: 'code', render: (i) => <span className="font-mono text-[12px] text-ink-900">{i.code}</span> },
    { key: 'name', label: 'Name', sortKey: 'name', render: (i) => (
      <div>
        <div className="text-ink-900">{i.name}</div>
        <div className="text-[11.5px] text-ink-400">{i.isShared ? 'Shared' : i.entityName}{i.isSystem ? ' · System' : ''}</div>
      </div>) },
    { key: 'invoiceType', label: 'Type', sortKey: 'invoiceType', render: (i) => typeLabel(i.invoiceType), hideOnMobile: true },
    { key: 'unitPriceMinor', label: 'Price inc. VAT', align: 'right', sortKey: 'unitPriceMinor', render: (i) => <Money minor={i.unitPriceMinor} currency={i.currency} /> },
    { key: 'taxCode', label: 'VAT', sortKey: 'taxCode', render: (i) => <span><span className="font-mono text-[12px]">{i.taxCode}</span> <span className="text-ink-400">{pct(i.taxRatePercent)}</span></span> },
    { key: 'incomeAccountCode', label: 'Account', hideOnMobile: true, render: (i) => i.incomeAccountCode ? <span>{i.incomeAccountCode} <span className="text-ink-400">{i.incomeAccountName}</span></span> : <span className="text-ink-300" title="Posts to the account the invoice type maps to">By type</span> },
    { key: 'isActive', label: 'Active', sortValue: (i) => (i.isActive ? 1 : 0), render: (i) => <Badge value={i.isActive ? 'Active' : 'Inactive'} tone={i.isActive ? 'good' : 'neutral'} /> },
  ];

  return (
    <div className="space-y-2.5">
      <PageHeader title="Items" menu={[
        { label: 'New item', icon: 'Plus', can: 'items.create', onClick: () => setEditing('new') },
        { label: 'Export CSV', export: { path: '/items', page: 'items', query: list.query } },
      ]} />
      <DataTable columns={columns} rows={list.rows} meta={list.meta} page={list.page} onPage={list.setPage} isLoading={list.isLoading} isFetching={list.isFetching}
        sort={list.sort} onSort={list.setSort} rowKey={(i) => i.id} onRowClick={(i) => setEditing(i)} search={list.search} onSearch={list.setSearch} searchPlaceholder="Code or name…"
        emptyTitle="No items" emptyBody="Add one with New item."
        filters={<ListFilter className="ml-auto" filters={list.filters} setFilters={list.setFilters} groups={[
          { key: 'invoiceType', label: 'Type', options: TYPES },
          { key: 'taxCode', label: 'VAT code', options: codes.map((c) => ({ value: c, label: c })) },
          { key: 'isActive', label: 'Active', as: 'boolean', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
        ]} />} />
      {list.error && <FormError error={list.error} />}
      {editing && <ItemModal item={editing === 'new' ? null : editing} rates={rates.data ?? []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void refresh(); }} />}
    </div>
  );
}

function ItemModal({ item, rates, onClose, onSaved }: { item: Item | null; rates: TaxRate[]; onClose: () => void; onSaved: () => void }) {
  const isNew = !item;
  const canEdit = useCan(isNew ? 'items.create' : 'items.update');
  const locked = !!item?.isSystem;
  const [form, setForm] = useState({
    code: item?.code ?? '', name: item?.name ?? '', description: item?.description ?? '', invoiceType: item?.invoiceType ?? 'RECHARGE',
    price: item ? (item.unitPriceMinor / 100).toFixed(2) : '', taxCode: item?.taxCode ?? '', incomeAccountId: item?.incomeAccountId ?? '',
    entityId: item?.entityId ?? '', isActive: item?.isActive ?? true,
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const entities = useQuery({ queryKey: ['entities', 'picker'], queryFn: async () => (await api.get<Entity[]>('/entities', { perPage: 200 })).data });
  const ukEntities = (entities.data ?? []).filter((e) => e.region === REGION);
  const accounts = useQuery({ queryKey: ['accounts', 'income'], queryFn: async () => (await api.get<Account[]>('/accounts', { accountType: 'INCOME', isPostable: true, isActive: true })).data });
  const ukIds = new Set(ukEntities.map((e) => e.id));
  // A company item picks from that company's chart. A shared item picks an account by code: each company posts to
  // its own account with that code, so one row per code is offered (keeping the item's current account for its code).
  const accountOptions = (() => {
    const all = (accounts.data ?? []).filter((a) => ukIds.has(a.entityId));
    if (form.entityId) return all.filter((a) => a.entityId === form.entityId);
    const byCode = new Map<string, Account>();
    for (const a of all) if (!byCode.has(a.code) || a.id === item?.incomeAccountId) byCode.set(a.code, a);
    return [...byCode.values()].sort((a, b) => a.code.localeCompare(b.code));
  })();
  const inForce = useMemo(() => rates.filter((r) => r.inForce).sort((a, b) => a.code.localeCompare(b.code)), [rates]);

  const priceMinor = form.price.trim() === '' ? 0 : Math.round(Number(form.price) * 100);
  const valid = form.code.trim() && form.name.trim() && form.taxCode && Number.isFinite(priceMinor) && priceMinor >= 0;

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        const body: CreateItemBody = {
          region: REGION, entityId: form.entityId || null, code: form.code.trim().toUpperCase(), name: form.name.trim(), description: form.description.trim() || null,
          unitPriceMinor: priceMinor, taxCode: form.taxCode, incomeAccountId: form.incomeAccountId || null, invoiceType: form.invoiceType, isActive: form.isActive,
        };
        return api.post<Item>('/items', body);
      }
      const body: UpdateItemBody = {};
      if (!locked && form.code.trim().toUpperCase() !== item.code) body.code = form.code.trim().toUpperCase();
      if (!locked && form.name.trim() !== item.name) body.name = form.name.trim();
      if (!locked && form.invoiceType !== item.invoiceType) body.invoiceType = form.invoiceType;
      if ((form.description.trim() || null) !== item.description) body.description = form.description.trim() || null;
      if (priceMinor !== item.unitPriceMinor) body.unitPriceMinor = priceMinor;
      if (form.taxCode !== item.taxCode) body.taxCode = form.taxCode;
      if ((form.incomeAccountId || null) !== item.incomeAccountId) body.incomeAccountId = form.incomeAccountId || null;
      if (form.isActive !== item.isActive) body.isActive = form.isActive;
      if (!Object.keys(body).length) return null;
      return api.patch<Item>(`/items/${item.id}`, body);
    },
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: async () => api.delete(`/items/${item!.id}`), onSuccess: onSaved });

  return (
    <Modal open onClose={onClose} title={isNew ? 'New item' : <span><span className="font-mono">{item.code}</span> · {item.name}</span>}
      footer={<>
        {!isNew && !locked && <Can do="items.delete"><Button variant="ghost" onClick={() => setConfirmDelete(true)} className="mr-auto">Delete</Button></Can>}
        <Button variant="secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
        {canEdit && <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>{isNew ? 'Add item' : 'Save'}</Button>}
      </>}>
      <div className="space-y-3">
        {locked && <p className="rounded-lg bg-ink-50 px-3 py-2 text-[12.5px] text-ink-600">A system item: one per invoice type, and it cannot be deleted. Its code, name and type are fixed; the price, description, VAT code, income account and active flag can change.</p>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!canEdit || locked} placeholder="KEY-REPLACEMENT" className="w-full font-mono" /></Field>
          <Field label="Type" hint="Sets the default income account">
            <Select value={form.invoiceType} onChange={(e) => setForm({ ...form, invoiceType: e.target.value as Item['invoiceType'] })} disabled={!canEdit || locked} className="w-full">
              {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!canEdit || locked} className="w-full" /></Field>
        <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!canEdit} className="w-full" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Price inc. VAT (${item?.currency ?? 'GBP'})`} hint="Including VAT at the item's rate"><Input type="number" min={0} step={0.01} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} disabled={!canEdit} placeholder="0.00" className="w-full" /></Field>
          <Field label="VAT code">
            <Select value={form.taxCode} onChange={(e) => setForm({ ...form, taxCode: e.target.value })} disabled={!canEdit} className="w-full">
              {!form.taxCode && <option value="">Choose…</option>}
              {inForce.map((r) => <option key={r.code} value={r.code}>{r.label} · {pct(r.ratePercent)}</option>)}
              {form.taxCode && !inForce.some((r) => r.code === form.taxCode) && <option value={form.taxCode}>{form.taxCode} (no rate in force)</option>}
            </Select>
          </Field>
        </div>
        <Field label="Income account" hint={form.entityId ? undefined : 'Each company posts to its own account with this code. Leave as "By type" to use the account the invoice type maps to.'}>
          <Select value={form.incomeAccountId} onChange={(e) => setForm({ ...form, incomeAccountId: e.target.value })} disabled={!canEdit} className="w-full">
            <option value="">By type (default)</option>
            {accountOptions.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
          </Select>
        </Field>
        <Field label="Available to" hint={isNew ? undefined : 'Fixed once the item exists'}>
          <Select value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value, incomeAccountId: '' })} disabled={!canEdit || !isNew} className="w-full">
            <option value="">Shared across companies</option>
            {ukEntities.map((e) => <option key={e.id} value={e.id}>{e.name} only</option>)}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-[13px] text-ink-700"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} disabled={!canEdit} /> Active (offered on new invoice lines)</label>
        <FormError error={save.error} />
      </div>
      {item && <Confirm open={confirmDelete} onClose={() => setConfirmDelete(false)} title={`Delete ${item.code}?`} confirmLabel="Delete item"
        body="The item is removed from the catalogue. An item already on an invoice or bill cannot be deleted; deactivate it instead."
        pending={remove.isPending} error={remove.error} onConfirm={() => remove.mutate()} />}
    </Modal>
  );
}
